import * as RadixTooltip from '@radix-ui/react-tooltip';
import { classNames } from '~/utils/styles';

import type { TooltipProps } from './Tooltip.types';
import { TOOLTIP_BUBBLE_CLASS_NAMES } from './tooltipBubbleClassNames';

export const Tooltip = ({
  align = 'center',
  children,
  className,
  side = 'bottom',
  trigger,
  triggerAsChild = true,
  triggerClassName,
  triggerDisabled,
  delayDuration = 300,
  disabled,
  open,
  onOpenChange,
  ...props
}: TooltipProps) => {
  const animationClasses = classNames(
    'relative -top-px will-change-[transform,opacity]',
    'data-[state=open]:data-[side=top]:animate-slideDownAndFade',
    'data-[state=open]:data-[side=right]:animate-slideLeftAndFade',
    'data-[state=open]:data-[side=bottom]:animate-slideUpAndFade',
    'data-[state=open]:data-[side=left]:animate-slideRightAndFade',
  );

  const contentClasses = classNames(
    // '[&_span]:h-1', // fixes tooltip arrow blurriness when rotated (left, right)
    `position-${side}-${align} z-20 focus:outline-hidden`,
    TOOLTIP_BUBBLE_CLASS_NAMES,
    animationClasses,
    className,
  );

  // Only while disabled: the rest of the time the control itself must be the trigger, so Radix's
  // `data-state` and `aria-describedby` land on it rather than on a wrapper the reader never visits.
  const triggerNode = triggerDisabled ? (
    <span
      className={classNames('inline-flex *:pointer-events-none', triggerClassName)}
      /* oxlint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- a role here would make the
         children presentational and swallow the disabled button's name */
      tabIndex={0}
    >
      {trigger}
    </span>
  ) : (
    trigger
  );

  return (
    <RadixTooltip.Provider delayDuration={delayDuration}>
      <RadixTooltip.Root onOpenChange={onOpenChange} open={open}>
        <RadixTooltip.Trigger asChild={triggerAsChild}>{triggerNode}</RadixTooltip.Trigger>
        {!disabled && (
          <RadixTooltip.Portal>
            <RadixTooltip.Content align={align} className={contentClasses} side={side} sideOffset={2} {...props}>
              {children}
              <RadixTooltip.Arrow className="fill-neutral-1000" data-testid="tooltip-arrow" />
            </RadixTooltip.Content>
          </RadixTooltip.Portal>
        )}
      </RadixTooltip.Root>
    </RadixTooltip.Provider>
  );
};
